import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

import BenchlingConfig from 'BenchlingConfig';
import {QueryingChartContent} from 'charts/chart-content/ChartContent';
import {DEFAULT_BLOCK_HEIGHT} from 'insights/InsightsConstants';
import {ProgressData} from 'llm/deep-research/messages/DeepResearchProgress';
import CitationSuperscript from 'llm/markdown/CitationSuperscript';
import {rehypeBenchlingTags} from 'llm/markdown/LLMMarkdownRehype';
import {remarkBenchlingTags} from 'llm/markdown/LLMMarkdownRemark';
import {BenchlingTagConfigs, BenchlingTagProperty} from 'llm/markdown/LLMMarkdownUtils';
import {remarkGfmTables} from 'llm/markdown/remarkTables';
import TableWithCitations from 'llm/markdown/TableWithCitations';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip, {isModelBenchChipSupported} from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {getAPIIDPrefix} from 'util/APIUtil';
import * as logging from 'util/Logging';

interface LLMMarkdownDependencies {
  onCitationClick?: (toolId: string, isSourceCitation?: boolean) => void;
  progressData?: ProgressData;
  citationNumberMap?: Map<string, number>;
}

// Factory function to create tag configs with dependencies
export const createBenchlingTagConfigs = (deps: LLMMarkdownDependencies): BenchlingTagConfigs => {
  const tagConfigs = [
    {
      markdownTag: 'bnch_id',
      htmlTagName: 'idchip',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => /^[a-z]+_[a-zA-Z0-9]{8,16}$/.test(content),
      component: ({idValue}: Record<string, unknown>) => {
        const id = idValue as string;
        if (id && isModelBenchChipSupported(id)) {
          return <BenchChip id={id} size={ChipSize.MINI} />;
        }
        const modelType = id ? getAPIIDPrefix(id) : undefined;
        logging.info(`[llm] Invalid Benchling ID`, {extra: {id, modelType}});
        return <span>{id}</span>;
      },
      preserveContentWhenSaving: true,
    },
    {
      markdownTag: 'bnch_citation',
      htmlTagName: 'citationchip',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => content.length > 0,
      component: ({idValue}: Record<string, unknown>) => {
        const citationId = idValue as string;
        const citationNumber = deps.citationNumberMap?.get(citationId);
        return (
          <CitationSuperscript
            citationId={citationId}
            citationNumber={citationNumber}
            onCitationClick={deps.onCitationClick}
            progressData={deps.progressData}
          />
        );
      },
      preserveContentWhenSaving: false,
    },
    // This is a hack to allow block elements like charts to be rendered.
    // This pattern should not be used.
    {
      markdownTag: 'p',
      htmlTagName: 'p',
      properties: [],
      validator: () => true,
      component: ({children}: Record<string, unknown>) =>
        BenchlingConfig.LLM_CUSTOM_PARAGRAPH_MARKDOWN ? (
          <div className={css(styles.paragraph)}>{children}</div>
        ) : (
          <p>{children}</p>
        ),
      preserveContentWhenSaving: true,
    },
    {
      markdownTag: 'bnch_chart',
      htmlTagName: 'benchlingchart',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => /^chart_[a-zA-Z0-9]{8,16}$/.test(content),
      component: ({idValue}: Record<string, unknown>) => {
        const chartId = idValue as string;
        return (
          <div className={css(styles.chartContainer)} data-chart-id={chartId}>
            <QueryingChartContent chartId={chartId} />
          </div>
        );
      },
      preserveContentWhenSaving: true,
    },
    {
      markdownTag: null,
      htmlTagName: 'table',
      properties: [],
      validator: (content: string) => content.length > 0,
      component: ({children}: Record<string, unknown>) => <TableWithCitations>{children}</TableWithCitations>,
      preserveContentWhenSaving: true,
    },
  ];
  if (BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA) {
    tagConfigs.push({
      markdownTag: 'web_citation',
      htmlTagName: 'webcitationchip',
      properties: [BenchlingTagProperty.LINK_URL],
      validator: (content: string) => {
        try {
          const url = new URL(content);
          return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (error) {
          // Expected: URL constructor throws for invalid URLs, return false for validation
          if (error instanceof TypeError) {
            return false;
          }
          throw error;
        }
      },
      component: ({linkUrl}: Record<string, unknown>) => {
        const originalUrl = linkUrl as string;
        // Strip all parameters from the URL as a security mitigation
        let cleanUrl = '';
        try {
          const url = new URL(originalUrl);
          cleanUrl = `${url.origin}${url.pathname}`;
        } catch (error) {
          if (error instanceof TypeError) {
            return <></>;
          }
          throw error;
        }

        return (
          <span className={css(styles.webCitationContainer)}>
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={css(styles.webCitationLink)}
            >
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.MINI}
                icon="link"
                data-testid={`web-citation-button-${cleanUrl}`}
              />
            </a>
          </span>
        );
      },
      preserveContentWhenSaving: true,
    });
  }
  return {tagConfigs};
};

const BASE_SAFE_MARKDOWN_TAGS = [
  // NOTE: We purposefully don't include a, img, script, etc. to limit risk.
  // headers
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  // text
  'p',
  'span',
  // rule & break
  'hr',
  'br',
  // code
  'code',
  'pre',
  // lists
  'li',
  'ul',
  'ol',
  // italics
  'em',
  // bold
  'strong',
  'b',
  // quotes
  'blockquote',
  'q',
  // strikethrough
  's',
  'del',
  // tables
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

// Creates safe markdown tags list including custom elements from config
const createSafeMarkdownTags = (tagConfigs: BenchlingTagConfigs): Array<string> => [
  ...BASE_SAFE_MARKDOWN_TAGS,
  ...tagConfigs.tagConfigs.map((config) => config.htmlTagName),
];

// Creates a sanitize schema for the markdown tags
export const createSanitizeSchema = (
  tagConfigs: BenchlingTagConfigs
): {
  tagNames: Array<string>;
  attributes: Record<string, Array<string>>;
} => ({
  tagNames: createSafeMarkdownTags(tagConfigs),
  attributes: {
    '*': [],
    ...Object.fromEntries(tagConfigs.tagConfigs.map((config) => [config.htmlTagName, config.properties])),
  },
});

export interface LLMMarkdownProps {
  markdown: string;
  onCitationClick?: (toolId: string, isSourceCitation?: boolean) => void;
  progressData?: ProgressData;
  citationNumberMap?: Map<string, number>;
}

export default function LLMMarkdownSection({
  markdown,
  onCitationClick,
  progressData,
  citationNumberMap,
}: LLMMarkdownProps): JSX.Element {
  const tagConfigs = createBenchlingTagConfigs({onCitationClick, progressData, citationNumberMap});
  const components = Object.fromEntries(
    tagConfigs.tagConfigs.map((config) => [config.htmlTagName, config.component])
  );
  const sanitizeSchema = createSanitizeSchema(tagConfigs);

  return (
    <section {...getTestProps({component: 'LLMMarkdownSection', element: 'root'})}>
      <ReactMarkdown
        allowedElements={sanitizeSchema.tagNames}
        remarkPlugins={[[remarkBenchlingTags, tagConfigs], remarkGfmTables]}
        rehypePlugins={[
          [rehypeBenchlingTags, tagConfigs],
          [rehypeSanitize, sanitizeSchema],
        ]}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
    </section>
  );
}

const styles = StyleSheet.create({
  webCitationContainer: {
    display: 'inline-block',
    marginLeft: '2px',
    marginRight: '2px',
  },
  webCitationLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
  chartContainer: {
    width: '100%',
    height: DEFAULT_BLOCK_HEIGHT,
    margin: '16px 0',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  paragraph: {
    margin: '1em 0',
    lineHeight: '1.5',
  },
});
